import { useEffect, useRef, useState, type FormEvent } from 'react'
import { MinimalAlert, MinimalButton, MinimalInput } from '@ovasabi/ui-minimal'
import { z } from 'zod'
import { commandPayload, commands } from '../../lib/runtime'
import { hundredths } from '../promotions/promotionModel'
import * as S from './OpsStyle'

const rateFields = [
  ['discount_bps', 'Meal-plan discount (%)'], ['commission_bps', 'Commission (%)'],
  ['service_charge_bps', 'Service charge (%)'], ['tax_bps', 'Tax (%)'],
] as const
const bps = z.coerce.number().int().min(0).max(10000)
const ratesSchema = z.object({ version: z.coerce.number().int().nonnegative(), discount_bps: bps,
  commission_bps: bps, service_charge_bps: bps, tax_bps: bps,
  weeks_per_cycle: z.coerce.number().int().positive(), min_chefs_per_zone: z.coerce.number().int().nonnegative() })
type Rates = z.infer<typeof ratesSchema>

export const OpsRatesPanel = () => {
  const [rates, setRates] = useState<Rates | null>(null)
  const [form, setForm] = useState<Record<string, string>>({})
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [attempt, setAttempt] = useState(0)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const pending = useRef(false)
  const apply = (value: Rates) => {
    setRates(value)
    setForm(Object.fromEntries(rateFields.map(([key]) => [key, String(value[key] / 100)])))
  }
  useEffect(() => {
    let active = true
    setLoading(true); setError(''); setRates(null)
    void commands.mealplan.getRates().then((current) => {
      if (active) apply(ratesSchema.parse(commandPayload(current)))
    }).catch(() => { if (active) setError('Could not load current rates. Retry before editing.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [attempt])
  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!rates || pending.current) return
    pending.current = true; setBusy(true); setError(''); setNotice('')
    try {
      const next = ratesSchema.parse({ ...rates, ...Object.fromEntries(rateFields.map(([key]) => [key, hundredths(form[key])])) })
      const payload = { ...Object.fromEntries(rateFields.map(([key]) => [key, next[key]])), weeks_per_cycle: next.weeks_per_cycle, min_chefs_per_zone: next.min_chefs_per_zone }
      const confirmed = ratesSchema.parse(commandPayload(await commands.mealplan.setRates(payload)))
      apply(confirmed); setNotice(`Rate version ${confirmed.version} saved. Existing packages keep their recorded rates.`)
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not save rates.') }
    finally { pending.current = false; setBusy(false) }
  }
  return <S.Card>
    <S.CardTitle>Chef meal-plan pricing</S.CardTitle>
    {loading && <p role="status">Loading pricing…</p>}
    {error && <MinimalAlert tone="danger" title="Pricing action failed">{error}<MinimalButton disabled={busy} onClick={() => setAttempt((n) => n + 1)}>Reload rates</MinimalButton></MinimalAlert>}
    {notice && <MinimalAlert tone="success" title="Saved">{notice}</MinimalAlert>}
    {rates && !loading && <S.Form onSubmit={save}>
      <S.Note>{rates.version === 0 ? 'Server defaults' : `Rate version ${rates.version}`} · {rates.weeks_per_cycle} weeks per cycle.</S.Note>
      <S.FormGrid>{rateFields.map(([key, label]) => <MinimalInput key={key} required label={label} value={form[key] ?? ''} inputMode="decimal" disabled={busy} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />)}</S.FormGrid>
      <S.Note>These percentage rates apply only to chef meal-plan packages. They do not change cart service fees. Delivery costs zero. Existing packages keep their saved rates.</S.Note>
      <MinimalButton type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save rate version'}</MinimalButton>
    </S.Form>}

  </S.Card>
}
